import React, { useState } from "react";
import { Formik, Form } from "formik";
import styled, { keyframes, css } from "styled-components";
import * as Yup from "yup";
import ActionBar from "../../common/ActionBar";
import _ from "lodash";
import { navigate } from "@reach/router";
import useLoader from "../../../hooks/useLoader";
import { useSnackbar } from "notistack";
import { getErrorMessage } from "../../../helpers/functions";
import { StyledRadio } from "../Field Mapping";
import { RadioGroup } from "@material-ui/core";
import MeetingService from "../../../services/MeetingService";

const validationSchema = Yup.object().shape({
  type: Yup.string().required().oneOf(["SMS", "Whatsapp", "Email"]),
  template_name: Yup.string().when("type", (type, schema) => {
    schema = schema.trim();
    if (type === "SMS" || type === "Whatsapp")
      return schema.required("Template name is required").min(3).max(100);
    return schema.nullable();
  }),
  dlt_template_id: Yup.string().when("type", {
    is: "SMS",
    then: (s) =>
      s
        .required("DLT Template ID is required")
        .matches(/^\d{10,}$/, "Must be numeric, 10+ digits"),
    otherwise: (s) => s.nullable().strip(),
  }),
  template: Yup.string().when("type", {
    is: "SMS",
    then: (s) => s.required("SMS content is required"),
    otherwise: (s) => s.nullable().strip(),
  }),
  components: Yup.string().when("type", {
    is: "Whatsapp",
    then: (s) => s.required("Message body/components required"),
    otherwise: (s) => s.nullable().strip(),
  }),
  name: Yup.string().when("type", {
    is: "Email",
    then: (s) => s.trim().required("Template name is required").min(3).max(120),
    otherwise: (s) => s.nullable().strip(),
  }),
  subject: Yup.string().when("type", {
    is: "Email",
    then: (s) => s.trim().required("Subject is required").max(200),
    otherwise: (s) => s.nullable().strip(),
  }),
  body: Yup.string().when("type", {
    is: "Email",
    then: (s) => s.required("Email body is required").max(15000),
    otherwise: (s) => s.nullable().strip(),
  }),
});

const getFieldsByType = (type) => {
  switch (type) {
    case "SMS":
      return [
        {
          name: "template_name",
          label: "Template Name",
          type: "text",
          placeholder: "e.g. OTP_Verification_v2",
          required: true,
        },
        /*
        {
          name: "dlt_template_id",
          label: "DLT Template ID",
          type: "text",
          placeholder: "10+ digit numeric ID",
          required: true,
        },
        */
        {
          name: "template",
          label: "Message Content",
          type: "textarea",
          placeholder: "Hi {name}, your OTP is {otp}. Valid for 10 minutes.",
          required: true,
          full: true,
        },
      ];
    case "Whatsapp":
      return [
        {
          name: "template_name",
          label: "Template Name",
          type: "text",
          placeholder: "e.g. order_confirmation",
          required: true,
        },
        {
          name: "components",
          label: "Message Body / Components",
          type: "textarea",
          placeholder:
            '{"header":{"type":"text","text":"Hello!"},"body":{"text":"Your order #{{1}} is confirmed."}}',
          required: true,
          full: true,
          mono: true,
        },
      ];
    case "Email":
      return [
        {
          name: "name",
          label: "Template Name",
          type: "text",
          placeholder: "e.g. Welcome Email",
          required: true,
        },
        {
          name: "subject",
          label: "Email Subject",
          type: "text",
          placeholder: "Welcome to the platform, {{first_name}}!",
          required: true,
        },
        {
          name: "body",
          label: "Email Body (HTML supported)",
          type: "textarea",
          placeholder: "<p>Hi {{name}},</p>\n<p>Welcome aboard!</p>",
          required: true,
          full: true,
          mono: true,
          rows: 10,
        },
      ];
    default:
      return [];
  }
};

const channelConfig = {
  SMS: {
    color: "#00838c",
    bg: "#FFFFFF",
    label: "SMS",
    desc: "DLT-compliant text messages",
  },
  Whatsapp: {
    color: "#00838c",
    bg: "#FFFFFF",
    label: "WhatsApp",
    desc: "Rich messaging via WhatsApp API",
  },
  Email: {
    color: "#00838c",
    bg: "#FFFFFF",
    label: "Email",
    desc: "HTML email campaigns",
  },
};

export default function Communication() {
  const [submitted, setSubmitted] = useState(false);
  const breadcrumbs = ["Comms Manager", "Communication"];
  const { setLoader } = useLoader();
  const enqueueSnackbar = useSnackbar();

  const initialValues = {
    type: "SMS",
    template_name: "",
    dlt_template_id: "1207161901234567890",
    template: "",
    language: "en",
    category: "UTILITY",
    components: "",
    name: "",
    slug: "welcome_email_template",
    subject: "",
    body: "",
  };

  async function onFormSubmit(values, { resetForm }) {
    try {
      setLoader({ state: true, message: "Loading..." });
      setSubmitted(true);
      let payload = {};
      if (values.type === "SMS") {
        payload = {
          SMS: {
            template_name: values.template_name,
            template: values.template,
            dlt_template_id: values.dlt_template_id
              ? values.dlt_template_id
              : "1207161901234567890",
            language: "en",
          },
        };
      } else if (values.type === "Whatsapp") {
        payload = {
          Whatsapp: {
            template_name: values.template_name,
            language: "en",
            category: "UTILITY",
            components: [
              {
                type: "BODY",
                text: values.components,
              },
            ],
          },
        };
      } else {
        payload = {
          Email: {
            name: values.name,
            slug: "welcome_email_template",
            subject: values.subject,
            body: values?.body,
            language: "en",
          },
        };
      }
      const response = await MeetingService.postCommunication(payload);
      if (response) {
        enqueueSnackbar(response?.message, {
          variant: "success",
        });
      }
    } catch (error) {
      const message = getErrorMessage(error);
      enqueueSnackbar(message, {
        variant: "error",
      });
    } finally {
      setLoader({ state: false });
      resetForm();
      setTimeout(() => setSubmitted(false), 3000);
    }
  }

  return (
    <>
      <ActionBar
        breadcrumbs={breadcrumbs}
        onBreadcrumbClick={(item) => {
          if (_.snakeCase(item) === "comms_manager") {
            navigate("/dashboard/case-manager/comms-manager");
          }
        }}
      />
      <PageWrapper>
        <Card>
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={onFormSubmit}
            validateOnBlur
            validateOnChange
            enableReinitialize
          >
            {({
              values,
              isSubmitting,
              handleSubmit,
              setFieldValue,
              touched,
              errors,
              handleBlur,
              handleChange,
              resetForm,
            }) => {
              const cfg = channelConfig[values.type];
              const fields = getFieldsByType(values.type);

              return (
                <Form onSubmit={handleSubmit}>
                  <SectionLabel>Choose channel</SectionLabel>
                  <ChannelGrid>
                    <RadioGroup
                      row
                      value={values.type}
                      onChange={(e) => setFieldValue("type", e.target.value)}
                    >
                      <StyledRadio value="SMS" label="SMS" />
                      <StyledRadio value="Whatsapp" label="Whatsapp" />
                      <StyledRadio value="Email" label="Email" />
                    </RadioGroup>

                    {/* {Object.entries(channelConfig).map(([val, c]) => (
                      <ChannelCard
                        key={val}
                        active={values.type === val}
                        color={c.color}
                        bg={c.bg}
                        type="button"
                        onClick={() => setFieldValue("type", val)}
                      >
                        <ChannelLabel>{c.label}</ChannelLabel>
                        <ChannelDesc>{c.desc}</ChannelDesc>
                        {values.type === val && <ActiveDot color={c.color} />}
                      </ChannelCard>
                    ))} */}
                  </ChannelGrid>

                  <Divider />

                  <SectionLabel>
                    <SectionDot color={cfg.color} />
                    {cfg.label} template details
                  </SectionLabel>

                  <FieldsGrid key={values.type}>
                    {fields.map((f, i) => (
                      <FieldWrapper key={f.name} full={f.full} delay={i * 0.07}>
                        <FieldLabel htmlFor={f.name}>
                          {f.label}
                          {f.required && <Required>*</Required>}
                        </FieldLabel>

                        {f.type === "textarea" ? (
                          <StyledTextarea
                            id={f.name}
                            name={f.name}
                            value={values[f.name] || ""}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            placeholder={f.placeholder}
                            rows={f.rows || 5}
                            mono={f.mono}
                            hasError={touched[f.name] && errors[f.name]}
                            accentColor={cfg.color}
                          />
                        ) : (
                          <StyledInput
                            id={f.name}
                            name={f.name}
                            type="text"
                            value={values[f.name] || ""}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            placeholder={f.placeholder}
                            hasError={touched[f.name] && errors[f.name]}
                            accentColor={cfg.color}
                          />
                        )}

                        {touched[f.name] && errors[f.name] && (
                          <ErrorMsg>{errors[f.name]}</ErrorMsg>
                        )}
                      </FieldWrapper>
                    ))}
                  </FieldsGrid>

                  <ActionsRow>
                    <ResetBtn type="button" onClick={() => resetForm()}>
                      Reset
                    </ResetBtn>
                    <SubmitBtn
                      type="submit"
                      onClick={handleSubmit}
                      color={cfg.color}
                      loading={isSubmitting}
                      success={submitted}
                    >
                      {isSubmitting ? (
                        <>
                          <Spinner /> Saving…
                        </>
                      ) : submitted ? (
                        <>Template Created</>
                      ) : (
                        <>Create Template</>
                      )}
                    </SubmitBtn>
                  </ActionsRow>
                </Form>
              );
            }}
          </Formik>
        </Card>
      </PageWrapper>
    </>
  );
}

const tokens = {
  ink: "#0A0A0F",
  inkSoft: "#3D3D52",
  inkMuted: "#8888A8",
  surface: "#F7F7FC",
  canvas: "#FFFFFF",
  tealDeep: "#00838C",
  tealGlow: "rgba(0, 194, 199, 0.18)",
  tealPale: "rgba(0, 194, 199, 0.07)",
  accent: "#FF5F7E",
  accentPale: "rgba(255, 95, 126, 0.09)",
  border: "rgba(0, 0, 0, 0.08)",
  borderFocus: "#00C2C7",
  shadow: "0 2px 16px rgba(0,0,0,0.07), 0 1px 4px rgba(0,0,0,0.04)",
  shadowLg: "0 20px 60px rgba(0,0,0,0.10), 0 4px 16px rgba(0,0,0,0.06)",
  radius: "16px",
  radiusSm: "10px",
  radiusXs: "7px",
  fontDisplay: "'Sora', 'Segoe UI', sans-serif",
  fontBody: "'DM Sans', 'Segoe UI', sans-serif",
  fontMono: "'JetBrains Mono', monospace",
};

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(18px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const slideIn = keyframes`
  from { opacity: 0; transform: translateX(-12px); }
  to   { opacity: 1; transform: translateX(0); }
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const PageWrapper = styled.div`
  min-height: 100vh;
  padding: 32px 16px 64px;
  font-family: openSans-SemiBold, sans-serif;

  @media (min-width: 640px) {
    padding: 40px 24px 64px;
  }
  @media (min-width: 1024px) {
    padding: 56px 40px 80px;
  }
`;

const Card = styled.div`
  max-width: 100%;
  margin: 0 auto;
  background: ${tokens.canvas};
  border-radius: ${tokens.radius};
  box-shadow: ${tokens.shadowLg};
  border: 1px solid ${tokens.border};
  overflow: hidden;
  animation: ${fadeUp} 0.5s 0.1s ease both;
  padding: 32px 24px;

  @media (min-width: 640px) {
    padding: 40px 36px;
  }
  @media (min-width: 1024px) {
    padding: 48px 52px;
  }
`;

const SectionLabel = styled.div`
  font-family: openSans-SemiBold, sans-serif;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #949ab0;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SectionDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${(p) => p.color};
  display: inline-block;
  box-shadow: 0 0 0 3px ${(p) => p.color}33;
`;

const ChannelGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
  margin-bottom: 32px;

  @media (min-width: 480px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const Divider = styled.div`
  height: 1px;
  background: linear-gradient(
    90deg,
    transparent,
    ${tokens.border} 20%,
    ${tokens.border} 80%,
    transparent
  );
  margin: 0 0 28px;
`;

const FieldsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 22px;
  margin-bottom: 36px;

  @media (min-width: 640px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const FieldWrapper = styled.div`
  grid-column: ${(p) => (p.full ? "1 / -1" : "span 1")};
  animation: ${slideIn} 0.35s ${(p) => p.delay || 0}s ease both;
`;

const FieldLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 6px;
  font-family: openSans-SemiBold, sans-serif;
  font-size: 13px;
  color: #949ab0;
  margin-bottom: 8px;
  cursor: pointer;
`;

const Required = styled.span`
  color: #d91b1b;
  margin-left: 2px;
`;

const inputBase = css`
  width: 100%;
  background: ${tokens.surface};
  border: 1.5px solid ${(p) => (p.hasError ? "#d91b1b" : tokens.border)};
  border-radius: ${tokens.radiusSm};
  font-family: openSans-SemiBold, sans-serif;
  font-size: ${(p) => (p.mono ? "13px" : "14.5px")};
  color: #293461;
  padding: 13px 16px;
  outline: none;
  transition: all 0.2s ease;
  box-sizing: border-box;
  line-height: 1.6;

  &::placeholder {
    color: #949ab0;
    font-size: 13px;
  }

  &:focus {
    border-color: ${(p) =>
      p.hasError ? "#f44336" : p.accentColor || "#949ab0"};
    background: ${tokens.canvas};
  }
`;

const StyledInput = styled.input`
  ${inputBase}
`;

const StyledTextarea = styled.textarea`
  ${inputBase}
  resize: vertical;
  min-height: 120px;
`;

const ErrorMsg = styled.div`
  margin-top: 6px;
  font-size: 10px;
  font-family: openSans-SemiBold, sans-serif;
  color: #f44336;
  display: flex;
  align-items: center;
  gap: 4px;
  animation: ${slideIn} 0.25s ease both;
`;

const ActionsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  flex-wrap: wrap;

  @media (max-width: 400px) {
    flex-direction: column-reverse;
    align-items: stretch;
    & > * {
      width: 100%;
      justify-content: center;
    }
  }
`;

const ResetBtn = styled.button`
  background: #ffffff;
  border: 1.5px solid #00838c;
  border-radius: ${tokens.radiusSm};
  padding: 12px 24px;
  font-family: openSans-SemiBold, sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: #00838c;
  cursor: pointer;
  transition: all 0.18s ease;
`;

const SubmitBtn = styled.button`
  background: #00838c;
  border: none;
  border-radius: ${tokens.radiusSm};
  padding: 13px 28px;
  font-family: openSans-SemiBold, sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: #ffffff;
  cursor: ${(p) => (p.loading ? "not-allowed" : "pointer")};
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.22s ease;
  opacity: ${(p) => (p.loading ? 0.85 : 1)};
  letter-spacing: 0.01em;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 8px 20px ${(p) => p.color || tokens.teal}44;
    filter: brightness(1.06);
  }
  &:active {
    transform: translateY(0);
  }
`;

const Spinner = styled.span`
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.35);
  border-top-color: #fff;
  border-radius: 50%;
  display: inline-block;
  animation: ${spin} 0.7s linear infinite;
`;
